import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, UserCog } from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardContent, Button,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '../ui';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import {
  getGroupStaff,
  getStaffByInscripcion,
  upsertStaffInscripcion,
  removeStaffInscripcion,
  precargarStaffInscripcion
} from '../../lib/supabase';
import {
  cupoStaff, rolPorDefecto, motivoStaff, ROL_TORNEO, ROL_TORNEO_LABEL
} from '../../lib/staffInscripcion';
import { cn, fixMojibake } from '../../lib/utils';

// El staff que el grupo lleva a un torneo de la RED. La organizadora lo ve junto al
// roster; el tope lo pone el torneo (max_staff_grupo) y el minimo es 1. Sin congelado.
// En el torneo interno esta pestana no existe: ahi el staff es el del grupo.
const StaffInscripcionTab = ({ inscripcionId, grupoId, orgId, tope, puedeEditar, onCambio }) => {
  const { alert, ConfirmDialog } = useConfirmDialog();
  const [grupoStaff, setGrupoStaff] = useState([]);
  const [declarados, setDeclarados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ocupado, setOcupado] = useState(null);

  const cargar = useCallback(async () => {
    if (!inscripcionId) return;
    const [gs, di] = await Promise.all([
      grupoId ? getGroupStaff(grupoId) : Promise.resolve({ data: [] }),
      getStaffByInscripcion(inscripcionId)
    ]);
    setGrupoStaff((gs.data || []).map((row) => (row.staff ? { ...row.staff, staff_id: row.staff_id } : row)));
    setDeclarados(di.data || []);
    setLoading(false);
  }, [inscripcionId, grupoId]);

  useEffect(() => { cargar(); }, [cargar]);

  const porStaff = useMemo(() => {
    const m = new Map();
    declarados.forEach((d) => m.set(d.staff_id, d));
    return m;
  }, [declarados]);
  const cupo = cupoStaff(declarados, tope);

  const conError = async (fn) => {
    try {
      const res = await fn();
      if (res?.error) {
        await alert('Error', motivoStaff(res.error));
        return;
      }
      await cargar();
      onCambio?.();
    } catch (err) {
      await alert('Error', motivoStaff(err));
    } finally {
      setOcupado(null);
    }
  };

  const alternar = (staffId, dentro) => {
    setOcupado(staffId);
    return conError(() => dentro
      ? removeStaffInscripcion(inscripcionId, staffId)
      : upsertStaffInscripcion({ inscripcionId, staffId, orgId, rolTorneo: rolPorDefecto(declarados) }));
  };

  const cambiarRol = (staffId, rolTorneo) => {
    setOcupado(staffId);
    return conError(() => upsertStaffInscripcion({ inscripcionId, staffId, orgId, rolTorneo }));
  };

  const precargar = () => {
    setOcupado('precarga');
    return conError(() => precargarStaffInscripcion(inscripcionId));
  };

  return (
    <Card className="border shadow-sm">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <UserCog className="h-4 w-4" /> Staff que va al torneo
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            <span className="font-medium text-foreground">{cupo.texto}</span>
            {cupo.tope ? ' · tope del torneo' : ' · el torneo no pone tope'}
            {' · '}la organizadora lo ve junto al roster. Mínimo uno: quien dirige.
          </p>
        </div>
        {puedeEditar && (
          <Button size="sm" variant="outline" onClick={precargar} disabled={ocupado !== null || cupo.lleno}>
            <RefreshCw className={cn('h-4 w-4 mr-1', ocupado === 'precarga' && 'animate-spin')} /> Precargar desde grupo
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Cargando staff...</p>
        ) : grupoStaff.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            El grupo no tiene staff asignado. Asígnalo en la ficha del grupo y vuelve aquí.
          </p>
        ) : (
          grupoStaff.map((s) => {
            const sid = s.staff_id || s.id;
            const fila = porStaff.get(sid);
            const dentro = Boolean(fila);
            const bloqueado = !puedeEditar || ocupado !== null || (!dentro && cupo.lleno);
            return (
              <div
                key={sid}
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3',
                  dentro ? 'bg-emerald-50/50 border-emerald-200' : 'bg-muted/20'
                )}
              >
                <input
                  type="checkbox"
                  aria-label={`${dentro ? 'Quitar' : 'Agregar'} ${s.nombre_completo || 'staff'}`}
                  checked={dentro}
                  onChange={() => alternar(sid, dentro)}
                  disabled={bloqueado}
                  className="h-4 w-4"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{fixMojibake(s.nombre_completo || 'Sin nombre')}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{s.rol || 'staff'}{s.telefono ? ` · ${s.telefono}` : ''}</p>
                </div>
                {dentro && (
                  <Select
                    value={fila.rol_torneo || ROL_TORNEO.ASISTENTE}
                    onValueChange={(v) => cambiarRol(sid, v)}
                    disabled={!puedeEditar || ocupado !== null}
                  >
                    <SelectTrigger className="h-8 w-[130px] text-xs" aria-label={`Rol de ${s.nombre_completo || 'staff'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(ROL_TORNEO).map((r) => (
                        <SelectItem key={r} value={r}>{ROL_TORNEO_LABEL[r]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })
        )}
      </CardContent>
      <ConfirmDialog />
    </Card>
  );
};

export default StaffInscripcionTab;
